import { useRef, useState } from 'react';
import type { BatchLead, BatchLeadNotesApi, BatchNote } from './types';
import { noteSaveFailureMessage, overlayNoteSaves, pruneNoteSaves, type NoteSave } from './leadNotes';
import { useNoteSaveRefresh } from './useNoteSaveRefresh';

/**
 * `useLeadNoteSync`: the Batch grid's Notes column save state, hosted by
 * `BatchScreen` like `useLeadRowSync` and for the same reason: the screen
 * unmounts the leads table while the operator is on the CRM Clean / Match /
 * Send tabs, and a note save in flight must survive that.
 *
 * Every grid note save goes through `save`, so its outcome lands here whether
 * or not the cell (or the table) that started it is still mounted:
 *
 *   - while it is out, `inFlight` says so, and a cell mounting for that lead
 *     (the table came back) shows it as still saving and takes its outcome —
 *     never "Add a note" over a create still out, which a second create
 *     would duplicate;
 *   - once confirmed, it is laid over any row that predates it (`overlay`)
 *     until a list load that started after it lands (`epoch` / `prune`), so
 *     a remounted table never shows the old note, and the next edit sends the
 *     saved note's version rather than a stale one the server 409s;
 *   - if it fails, `failure` keeps the text, the reason and the note the
 *     edit started from until the operator closes the box or saves again,
 *     so a cell mounting after the failure reopens with them — and a retry
 *     there still sends the version it started from (a 409 stays a 409).
 */

export type NoteSaveKind = 'create' | 'edit';

/** A save that is out. */
export interface NoteSaveInFlight {
  kind: NoteSaveKind;
  /** The note it edits (its `serverVersion` is the lock), or null for a create. */
  base: BatchNote | null;
  /** The text being saved, as the cell showed it. */
  text: string;
  /** Settles with the save: the saved note, or the API's error. */
  promise: Promise<BatchNote>;
}

/** A save that gave up, kept until the operator deals with it. */
export interface NoteSaveFailure {
  kind: NoteSaveKind;
  /**
   * The note it edited, as the edit started: a retry sends this same
   * version, so after a 409 it can only conflict again — never overwrite a
   * colleague's newer note that a later load has brought in.
   */
  base: BatchNote | null;
  text: string;
  /** What the cell says (`noteSaveFailureMessage`). */
  message: string;
}

export interface LeadNoteSyncStore {
  /**
   * Saves `text` as the lead's note: an edit of `base` (its id and
   * `serverVersion` are the version lock), or a new note when `base` is
   * null. `conferenceId` is the event it was made under (its tiles refresh).
   * Resolves with the saved note; rejects with the API's error.
   */
  save: (
    api: BatchLeadNotesApi,
    leadId: string,
    base: BatchNote | null,
    text: string,
    meta?: { conferenceId?: string | null },
  ) => Promise<BatchNote>;
  inFlight: (leadId: string) => NoteSaveInFlight | null;
  failure: (leadId: string) => NoteSaveFailure | null;
  /** The operator closed the box, or saved again: the failure is dealt with. */
  clearFailure: (leadId: string) => void;
  /** Confirmed saves shown over the rows that predate them. `rows` itself when none apply. */
  overlay: (rows: BatchLead[]) => BatchLead[];
  /** Read before a list load starts; pass the value to `prune` once it lands. */
  epoch: () => number;
  /**
   * Forget confirmed saves the load that started at `epoch` already carries.
   * Silent on purpose: call it beside the `setLeads` of that load, whose
   * render reads the pruned state; a render of its own in between would show
   * the old rows without the saves.
   */
  prune: (epoch: number) => void;
  subscribe: (listener: () => void) => () => void;
  /** Changes on every state change (useSyncExternalStore's snapshot). */
  getVersion: () => number;
  /**
   * Hears every confirmed save, for a host that keeps rows of its own in step
   * (the unified CRM grid's shared rows): lay it over a row with
   * `overlayNoteSaves`, which never counts a create twice.
   */
  onConfirmed: (listener: (leadId: string, save: NoteSave) => void) => () => void;
}

export interface LeadNoteSyncOptions {
  /** After every confirmed save (the hook's query invalidations). */
  onSaved?: (leadId: string, conferenceId: string | null) => void;
}

/** The framework-free store. `useLeadNoteSync` wraps it for React. */
export function createLeadNoteSyncStore(opts: LeadNoteSyncOptions = {}): LeadNoteSyncStore {
  const confirmed = new Map<string, NoteSave>();
  const inFlight = new Map<string, NoteSaveInFlight>();
  const failures = new Map<string, NoteSaveFailure>();
  const listeners = new Set<() => void>();
  const confirmListeners = new Set<(leadId: string, save: NoteSave) => void>();
  let version = 0;
  let seq = 0;

  function changed() {
    version += 1;
    for (const l of Array.from(listeners)) l();
  }

  return {
    save(api, leadId, base, text, meta) {
      const kind: NoteSaveKind = base ? 'edit' : 'create';
      const conferenceId = meta?.conferenceId ?? null;
      failures.delete(leadId);
      const promise = (base ? api.editNote(leadId, base, text) : api.createNote(leadId, text)).then(
        (saved) => {
          if (inFlight.get(leadId)?.promise === promise) inFlight.delete(leadId);
          const prev = confirmed.get(leadId);
          // Created stays true across a later edit until a load has caught
          // up, so a load that missed the create still counts it (the
          // overlay checks the note's id before adding anything).
          const save: NoteSave = { seq: ++seq, note: saved, created: kind === 'create' || !!prev?.created };
          confirmed.set(leadId, save);
          changed();
          for (const l of Array.from(confirmListeners)) l(leadId, save);
          opts.onSaved?.(leadId, conferenceId);
          return saved;
        },
        (err: unknown) => {
          if (inFlight.get(leadId)?.promise === promise) inFlight.delete(leadId);
          failures.set(leadId, { kind, base, text, message: noteSaveFailureMessage(err, kind) });
          changed();
          throw err;
        },
      );
      // The failure is kept above; a caller that went away must not leave
      // the rejection unhandled.
      promise.catch(() => undefined);
      inFlight.set(leadId, { kind, base, text, promise });
      changed();
      return promise;
    },

    inFlight: (leadId) => inFlight.get(leadId) ?? null,

    failure: (leadId) => failures.get(leadId) ?? null,

    clearFailure(leadId) {
      if (failures.delete(leadId)) changed();
    },

    overlay: (rows) => overlayNoteSaves(rows, confirmed),

    epoch: () => seq,

    prune(epoch) {
      pruneNoteSaves(confirmed, epoch);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getVersion: () => version,

    onConfirmed(listener) {
      confirmListeners.add(listener);
      return () => {
        confirmListeners.delete(listener);
      };
    },
  };
}

/**
 * The store for React: one per host component, stable for its lifetime. A
 * confirmed save refreshes the lead's detail at once and the event's KPI
 * tiles once per burst (`useNoteSaveRefresh`), from the host, so a save that
 * lands after the table unmounted still refreshes them.
 */
export function useLeadNoteSync(): LeadNoteSyncStore {
  const refresh = useNoteSaveRefresh();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const [store] = useState(() =>
    createLeadNoteSyncStore({
      onSaved: (leadId, conferenceId) => refreshRef.current(leadId, conferenceId),
    }),
  );
  return store;
}
